import _get from "lodash/get";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { FORM_ID } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { HardwareLookedUpProduct } from "../../../../../models/state";
import { get as Ajax_get, put as Ajax_put } from "../../../app/Ajax";
import { setActiveSubscription } from "../../../checkout/state/actions/cartActions";
import { getAllHardwareBundleItems } from "../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { actionTypes as RF_AT } from "../../../rapidform";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { clearAll } from "../../../rapidform/state/actions";
import { API_URL_ADD_EXTERNAL_HARDWARE, API_URL_GET_PRODUCTS_BY_EAN } from "../../api";
import * as AT from "../actionTypes";
import { getFormById } from "../selectors/formSelectors";

export const fetchProductByEan =
    (
        ean: string,
    ): ThunkAction<
        Promise<{ products?: HardwareLookedUpProduct[]; selectedEan?: string } | null>,
        RootState,
        unknown,
        AnyAction
    > =>
    async (dispatch) => {
        const response = Ajax_get<{ products?: HardwareLookedUpProduct[]; selectedEan?: string } | null>(
            {
                url: API_URL_GET_PRODUCTS_BY_EAN(ean),
            },
            dispatch,
        );

        response.then((data) => {
            const product = data?.products?.find((p) => p.ean === data?.selectedEan);
            if (product) {
                dispatch({
                    type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
                    payload: {
                        errorMessage: undefined,
                        form: FORM_ID.EQUIPMENT_REGISTRATION,
                        inputName: "equipment-hardwareModel",
                        inputValue: product.model,
                        visible: false,
                    },
                });
            }
            return product;
        });

        dispatch({
            type: AT.RETAILER_FETCH_PRODUCT_BY_EAN,
            payload: response,
            meta: { ean },
        });

        return response;
    };

export const updateProductAndEan =
    (ean: string, model: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-hardwareModel",
                inputValue: model,
                visible: false,
            },
        });
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-ean",
                inputValue: ean,
                visible: false,
            },
        });
        dispatch({
            type: AT.RETAILER_SET_SELECTED_EAN,
            ean,
        });
    };

export const addExternalHardware =
    (
        cartItemId: string,
        financingDuration: number,
        imei?: string,
    ): ThunkAction<Promise<{ success: boolean; message?: string }>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const form = getFormById(state, FORM_ID.EQUIPMENT_REGISTRATION);
        const model = _get(form, `values.equipment.${VALIDATION_FIELD.HARDWARE_MODEL}`, "");
        const hardwarePrice = _get(form, `values.equipment.${VALIDATION_FIELD.HARDWARE_PRICE}`, "");
        const ean = _get(form, "values.equipment.ean", "");

        const response = Ajax_put<{ product: HardwareLookedUpProduct }>(
            {
                url: API_URL_ADD_EXTERNAL_HARDWARE,
                data: {
                    cartItemId,
                    model,
                    ean,
                    totalPrice: hardwarePrice,
                    financingDuration,
                    imei,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.RETAILER_ADD_EXTERNAL_HARDWARE,
            payload: response,
        });

        return response
            .then(() => ({ success: true, message: undefined }))
            .catch((err: unknown) => ({
                success: false,
                message:
                    err && typeof err === "object" && "displayableMessage" in err
                        ? String((err as { displayableMessage?: unknown }).displayableMessage)
                        : undefined,
            }));
    };

export const changeExternalHardware =
    (cartItemId: string): ThunkAction<{ success: boolean }, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const allHardwareBundles = getAllHardwareBundleItems(state);
        const subscriptions = getSubscriptions(state);
        const subscription = subscriptions[cartItemId];

        if (!subscription) {
            return { success: false };
        }

        const bundle = allHardwareBundles.find((b) => b.bundleId === cartItemId);

        if (!bundle) {
            return { success: false };
        }

        dispatch(setActiveSubscription(cartItemId));
        dispatch(clearAll(FORM_ID.EQUIPMENT_REGISTRATION));

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-imei",
                inputValue: bundle.imei,
                visible: false,
            },
        });
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-hardwareModel",
                inputValue: bundle.name,
                visible: false,
            },
        });

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-ean",
                inputValue: bundle.ean || "",
                visible: false,
            },
        });
        dispatch({
            type: AT.RETAILER_SET_SELECTED_EAN,
            ean: bundle.ean,
        });
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-hardwarePrice",
                inputValue: String(bundle.price.total),
                visible: false,
            },
        });
        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.EQUIPMENT_REGISTRATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.EQUIPMENT_REGISTRATION,
                inputName: "equipment-hardwareCommitmentDuration",
                inputValue: subscription.financing?.duration?.toString() || "12",
                visible: false,
            },
        });

        return { success: true };
    };
